import { RichTextEditor } from "@mantine/tiptap";
import {
  Button,
  Group,
  Popover,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconLink, IconExternalLink } from "@tabler/icons-react";
import { useState } from "react";

const URL_PATTERN = /^https?:\/\/.+$/i;

const LinkControl = ({ editor }) => {
  const [opened, setOpened] = useState(false);
  const [value, setValue] = useState("");
  const [openInNewTab, setOpenInNewTab] = useState(true);
  const [touched, setTouched] = useState(false);

  const trimmed = value.trim();
  const isEmpty = trimmed === "";
  const isValid = URL_PATTERN.test(trimmed);
  const showError = touched && !isEmpty && !isValid;

  const open = () => {
    const attrs = editor?.getAttributes("link") || {};
    setValue(attrs.href || "");
    setOpenInNewTab(attrs.target ? attrs.target === "_blank" : true);
    setTouched(false);
    setOpened(true);
  };

  const save = () => {
    if (isEmpty) {
      editor?.chain().focus().extendMarkRange("link").unsetLink().run();
      setOpened(false);
      return;
    }
    if (!isValid) {
      setTouched(true);
      return;
    }
    editor
      ?.chain()
      .focus()
      .extendMarkRange("link")
      .setLink({
        href: trimmed,
        target: openInNewTab ? "_blank" : null,
        rel: openInNewTab ? "noopener noreferrer" : null,
      })
      .run();
    setOpened(false);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom"
      withArrow
      trapFocus
      shadow="md"
    >
      <Popover.Target>
        <RichTextEditor.Control
          onClick={() => (opened ? setOpened(false) : open())}
          active={editor?.isActive("link")}
          aria-label="Insert link"
          title="Insert link"
        >
          <IconLink size={16} stroke={1.5} />
        </RichTextEditor.Control>
      </Popover.Target>
      <Popover.Dropdown>
        <Group gap="xs" align="flex-start">
          <TextInput
            placeholder="https://example.com"
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            onBlur={() => setTouched(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                save();
              }
            }}
            error={showError && "URL must start with http:// or https://"}
            size="xs"
            style={{ minWidth: 240 }}
            data-autofocus
            rightSection={
              <Tooltip
                label={openInNewTab ? "Open in new tab" : "Open in same tab"}
                withArrow
              >
                <UnstyledButton
                  onClick={() => setOpenInNewTab((v) => !v)}
                  data-active={openInNewTab || undefined}
                  aria-label={
                    openInNewTab ? "Open in new tab" : "Open in same tab"
                  }
                  aria-pressed={openInNewTab}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    opacity: openInNewTab ? 1 : 0.4,
                  }}
                >
                  <IconExternalLink size={14} />
                </UnstyledButton>
              </Tooltip>
            }
          />
          <Button size="xs" onClick={save} disabled={!isEmpty && !isValid}>
            Save
          </Button>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
};

export default LinkControl;
